import type { WaiverAnswers } from '@/domain/types';

/**
 * The Addikted to Ink pre-session waiver as data — the single source of the
 * legal text, transcribed from the printed form (docs/addicted_to_ink_waiver.txt)
 * and lightly copy-edited for screen. Rendered identically on the customer signing
 * page and the front-desk modal. Bump `version` when the wording changes; the
 * signed value is stamped onto each Waiver so which text was agreed stays auditable.
 */

export interface WaiverClause {
  id: string;
  text: string;
}

export interface WaiverMedicalItem {
  id: string;
  label: string;
  /** Which WaiverAnswers array a ticked item lands in. */
  bucket: 'condition' | 'medication';
}

export const WAIVER_DOCUMENT = {
  version: 'v1-2026',
  studioName: 'Addikted to Ink',
  preamble:
    'In consideration of receiving a tattoo from Addikted to Ink™, I am over the age of 18 years and I agree to the following.',
  riskDisclosure:
    'I have been fully informed of the inherent risks associated with getting a tattoo. I understand that these risks — known and unknown — can lead to injury including (but not limited to) infection, scarring, difficulties in detecting melanoma, and allergic reactions to tattoo pigment, latex gloves and/or soap. Having been informed of these risks, I still wish to proceed with the tattoo and freely accept and expressly assume any and all risks that may arise from tattooing.',
  clauses: [
    {
      id: 'waive-release',
      text: 'I waive and release, to the fullest extent permitted by law, each of the Artists and the Tattoo Studio from all liability whatsoever, for any and all claims or causes of action that I, my estate, heirs, executors or assigns may have for personal injury or otherwise — including any direct and/or consequential damages — which result or arise from the application of my tattoo, whether caused by the negligence or fault of either the Artist or the Tattoo Studio or otherwise.',
    },
    {
      id: 'questions-answered',
      text: 'Both the Artist and Tattoo Studio have given me the full opportunity to ask any and all questions about the application of my tattoo, and all of my questions have been answered to my total satisfaction.',
    },
    {
      id: 'aftercare',
      text: "The Artist and Tattoo Studio have given me instructions on the aftercare of my tattoo whilst it is healing; I understand them and will follow them. I acknowledge that the tattoo can become infected, particularly if I do not follow these instructions. If any touch-up work is needed due to my own negligence, I agree that the work will be done at my own expense.",
    },
    {
      id: 'not-under-influence',
      text: 'I am not under the influence of alcohol and/or drugs, and I am voluntarily submitting myself to be tattooed by the Artist without duress or coercion.',
    },
    {
      id: 'meaning-spelling',
      text: 'Neither the Artist nor the Tattoo Studio is responsible for the meaning or spelling of the symbol or text that I have provided to them or chosen from the flash designs.',
    },
    {
      id: 'colour-fading',
      text: 'Variations in colour and design may exist between the tattoo art I have selected and the actual tattoo when applied to my body. I understand that over time the colours and clarity of my tattoo will fade due to unprotected exposure to the elements and the natural dispersion of pigment under the skin.',
    },
    {
      id: 'legal-fees-jurisdiction',
      text: 'I agree to reimburse the Artist and Tattoo Studio for any legal fees and costs incurred in any legal action I bring against them in which they are the prevailing party. I agree that the courts of Victoria, Australia shall have personal jurisdiction and venue over me, and exclusive jurisdiction for litigating any dispute arising out of or related to this agreement.',
    },
    {
      id: 'read-understood',
      text: 'I acknowledge that I have been given adequate opportunity to read and understand this document, which was not presented to me at the last minute, and I understand that I am signing a legal contract waiving certain rights to recover against the Artist or Tattoo Studio.',
    },
    {
      id: 'severability',
      text: 'If any provision of this release is found to be unenforceable or invalid, that portion shall be severed from this contract and the remainder will be construed as though the unenforceable portion had never been contained in this document.',
    },
    {
      id: 'age-declaration',
      text: 'I declare that I am 18 years of age or older (having provided valid evidence of proof of age) and am competent to sign this agreement.',
    },
  ] satisfies WaiverClause[],
  /** The negative medical declaration, presented as a tick-if-it-applies checklist. */
  medicalIntro:
    'Please tell us if any of the following apply to you. If none apply, leave them unticked — you are declaring that you do not have these conditions.',
  medicalConditions: [
    { id: 'diabetes', label: 'Diabetes', bucket: 'condition' },
    { id: 'epilepsy', label: 'Epilepsy', bucket: 'condition' },
    { id: 'haemophilia', label: 'Haemophilia', bucket: 'condition' },
    { id: 'heart-condition', label: 'Heart condition', bucket: 'condition' },
    { id: 'blood-thinners', label: 'Blood-thinning medication', bucket: 'medication' },
    { id: 'pregnant-breastfeeding', label: 'Pregnant or breastfeeding', bucket: 'condition' },
    { id: 'blood-borne', label: 'Blood infection or disease', bucket: 'condition' },
    { id: 'transplant', label: 'Organ / bone-marrow transplant', bucket: 'condition' },
    { id: 'mental-impairment', label: 'A condition affecting my judgement', bucket: 'condition' },
  ] satisfies WaiverMedicalItem[],
  photoRelease: {
    text: 'I release all rights to any photographs taken of me and the tattoo and consent to their reproduction in print and/or electronic form. Untick this if you do NOT want the studio to photograph you or your tattoo.',
  },
  closing:
    'Addikted to Ink do not tattoo juveniles, even with parental consent and guidance. I have read this agreement, I understand it, and I agree to be bound by it.',
  signatureIntent: 'By typing my full name below I agree that this is my legal signature.',
  jurisdiction: 'Victoria, Australia',
} as const;

/** All acknowledgement-clause ids — used when the customer agrees to the whole set. */
export const ALL_WAIVER_CLAUSE_IDS: string[] = WAIVER_DOCUMENT.clauses.map((c) => c.id);

/**
 * Map the medical-declaration checklist selections into the three WaiverAnswers
 * arrays that drive the customer flags. Ticked items land in `conditions` or
 * `medications` by their `bucket`; free-text allergies and an "anything else"
 * note are folded in. Pure — safe to unit-test and reuse on both sign surfaces.
 */
export function medicalAnswersFromChecklist(opts: {
  conditionIds: string[];
  allergies: string[];
  other?: string;
}): Pick<WaiverAnswers, 'allergies' | 'conditions' | 'medications'> {
  const conditions: string[] = [];
  const medications: string[] = [];
  for (const id of opts.conditionIds) {
    const item = WAIVER_DOCUMENT.medicalConditions.find((c) => c.id === id);
    if (!item) continue;
    (item.bucket === 'medication' ? medications : conditions).push(item.label);
  }
  const other = opts.other?.trim();
  if (other) conditions.push(other);
  return {
    allergies: opts.allergies.map((a) => a.trim()).filter(Boolean),
    conditions,
    medications,
  };
}
